"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.walkGenericOnly = exports.isGenericNode = exports.GENERIC_CLASS_RE = void 0;
const selector_1 = require("./selector");
/**
 * `TableLayout`/`TableRow` are deliberately EXCLUDED: a table row's
 * identity/content is often the only thing distinguishing two otherwise-similar
 * issues, so treating it as an anonymous container risks conflating rows.
 */
exports.GENERIC_CLASS_RE = /(?:^|\.)(View|ViewGroup|FrameLayout|LinearLayout|RelativeLayout|ConstraintLayout|CoordinatorLayout|ScrollView|HorizontalScrollView|NestedScrollView|GridLayout|GridView|UIView|UIScrollView|UIStackView|UICollectionView|UITableView)$/;
/**
 * True when `view` is a structural container carrying no identity of its own.
 * The `genericElement` role path counts two more fields as identity than the
 * className path does, because a labelled group is a `genericElement` with a
 * name and must survive.
 *
 * This is a yes/no on the node's OWN attributes. Re-inclusion when a generic
 * node's CHILDREN carry informative text lives in `walkGenericOnly`'s
 * `childEnrichFn`, which has the tree context this stateless check does not.
 */
const isGenericNode = (view) => {
    const className = view.className || '';
    if (typeof className === 'string' && exports.GENERIC_CLASS_RE.test(className)) {
        return !(view.viewIdResourceName || view.label || view.contentDescription);
    }
    const calculatedProps = view.calculatedProps && typeof view.calculatedProps === 'object'
        ? view.calculatedProps
        : {};
    const role = calculatedProps.role || view.calculatedRole || '';
    if (role === 'genericElement') {
        return !(view.viewIdResourceName ||
            view.label ||
            view.contentDescription ||
            calculatedProps.name ||
            view.accessibilityIdentifier);
    }
    return false;
};
exports.isGenericNode = isGenericNode;
/**
 * Walk parents from `viewId`, returning a root-first list of every ancestor
 * except the generic empty containers. The leaf contributes no entry and does
 * not count against `maxDepth`; when the cap bites, the entries kept are the
 * ones closest to the leaf.
 *
 * Nav-bar and hidden nodes are deliberately preserved — the ancestry still has
 * to carry position-and-role information. The only thing dropped is structural
 * noise (anchorless `UIView`/`UIStackView`/`FrameLayout` chains).
 *
 * Entries accumulate leaf-to-root and are reversed at the end, so the first
 * `maxDepth` appended are exactly the `maxDepth` the cap keeps — hence the
 * early break rather than a climb to the root and a slice.
 */
const walkGenericOnly = (options) => {
    const { viewId, tree, maxDepth, ...opts } = options;
    const ancestors = [];
    const visited = new Set();
    let current = tree.getView(viewId);
    let isLeaf = true;
    while (current) {
        const currentId = String(current.axeViewId || '');
        if (visited.has(currentId)) {
            break;
        }
        visited.add(currentId);
        if (isLeaf) {
            // The walk cannot simply start at the parent: the leaf's id has to enter
            // `visited` first, or a node whose parent is itself climbs one level
            // further before the cycle check fires.
            isLeaf = false;
        }
        else if (!(0, exports.isGenericNode)(current)) {
            const selector = (0, selector_1.generateViewSelector)(current, opts.selectorConfig, opts.normalizer ?? null);
            ancestors.push(selector);
        }
        else if (opts.childEnrichFn !== undefined) {
            const desc = opts.childEnrichFn(currentId);
            if (desc) {
                ancestors.push(desc);
            }
        }
        if (maxDepth !== null && ancestors.length === maxDepth) {
            break;
        }
        current = tree.getParent(currentId);
    }
    ancestors.reverse();
    return ancestors;
};
exports.walkGenericOnly = walkGenericOnly;
//# sourceMappingURL=ancestry.js.map